import { ArrowLeft, Ban, Download, HardDrive, History, Lock, Play, RotateCcw, TriangleAlert } from "lucide-react";
import { Tabs } from "radix-ui";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { ApiRequestError, useApi } from "../api/client";
import type { Snapshot, SnapshotErrors } from "../api/types";
import { useActivity, useDisks, useGroups, useInvalidateOrg, useMachine, usePolicies, useSnapshots } from "../api/queries";
import { common } from "../i18n/common";
import { useT } from "../i18n/core";
import { useFormat } from "../lib/format";
import { retentionText } from "../lib/policy";
import { osLabel } from "../lib/status";
import { ErrorState, LoadingRows, Panel } from "../ui/Common";
import { Dialog } from "../ui/Dialog";
import { JobPill, ProtectionPill } from "../ui/Pills";
import { RestoreStrip, RestoreStripLegend } from "../ui/RestoreStrip";
import { useToast } from "../ui/Toasts";
import { MachineDatabases } from "./MachineDatabases";
import { MachineShares } from "./MachineShares";
import { machineDetailMessages } from "./machineDetail.i18n";

export function MachineDetailPage() {
  const { orgId = "", machineId = "" } = useParams();
  const api = useApi();
  const toast = useToast();
  const invalidate = useInvalidateOrg();
  const m = useMachine(orgId, machineId);
  const snaps = useSnapshots(orgId, machineId);
  const jobs = useActivity(orgId, machineId);
  const policies = usePolicies(orgId);
  const groups = useGroups(orgId);
  const disks = useDisks(orgId, machineId);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  // Discos marcados para a imagem (ADR 0033): estado do servidor; marcar salva na hora.
  const [savingDisks, setSavingDisks] = useState(false);
  const [busy, setBusy] = useState(false);
  const t = useT(machineDetailMessages);
  const tc = useT(common);
  const f = useFormat();

  if (m.isPending) return <div className="panel"><LoadingRows rows={8} /></div>;
  if (m.isError) return <ErrorState error={m.error} />;
  const machine = m.data;
  const policy = policies.data?.find((p) => p.id === machine.policyId);
  const group = groups.data?.find((g) => g.id === machine.groupId);
  const revoked = machine.status === "revoked";
  const inventory = disks.data?.disks ?? [];
  const imageDisks = machine.imageDisks ?? [];
  const images = snaps.data?.filter((s) => s.kind === "image") ?? [];
  const files = snaps.data?.filter((s) => s.kind !== "image" && s.kind !== "database") ?? [];
  const dbPoints = snaps.data?.filter((s) => s.kind === "database") ?? [];
  const sharePoints = snaps.data?.filter((s) => s.kind === "share") ?? [];

  async function backupNow() {
    setBusy(true);
    try {
      await api.startBackup(orgId, machineId);
      toast("ok", t.backupSent(machine.hostname));
      invalidate(orgId);
    } catch (e) {
      toast("error", e instanceof ApiRequestError ? e.message : t.backupFailed);
    } finally {
      setBusy(false);
    }
  }

  async function imageNow() {
    setBusy(true);
    try {
      await api.startImage(orgId, machineId, []);
      toast("ok", t.imageSent(machine.hostname));
      invalidate(orgId);
    } catch (e) {
      toast("error", e instanceof ApiRequestError ? e.message : t.imageFailed);
    } finally {
      setBusy(false);
    }
  }

  function downloadVolume(snapshotId: string, volume: string) {
    // O servidor manda o volume bruto; o navegador grava direto em disco.
    window.location.assign(`/api/v1/orgs/${encodeURIComponent(orgId)}/snapshots/${encodeURIComponent(snapshotId)}/volumes/${encodeURIComponent(volume)}`);
    toast("ok", t.downloadStarted);
  }

  async function saveDisks(next: string[]) {
    setSavingDisks(true);
    try {
      await api.updateMachine(orgId, machineId, { imageDisks: next });
      toast("ok", t.disksSaved);
      invalidate(orgId);
    } catch (e) {
      toast("error", e instanceof ApiRequestError ? e.message : t.disksSaveFailed);
    } finally {
      setSavingDisks(false);
    }
  }

  async function changePolicy(policyId: string) {
    setBusy(true);
    try {
      await api.updateMachine(orgId, machineId, { policyId });
      toast("ok", t.policyChanged);
      invalidate(orgId);
    } catch (e) {
      toast("error", e instanceof ApiRequestError ? e.message : t.policyChangeFailed);
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    setBusy(true);
    try {
      await api.revokeMachine(orgId, machineId);
      toast("ok", t.revoked(machine.hostname));
      setConfirmRevoke(false);
      invalidate(orgId);
    } catch (e) {
      toast("error", e instanceof ApiRequestError ? e.message : t.revokeFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <header className="page-head">
        <div style={{ minWidth: 0 }}>
          <Link to={`/o/${orgId}/machines`} className="eyebrow" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <ArrowLeft size={12} aria-hidden="true" /> {t.machines}
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
            <h1 className="mono" style={{ fontFamily: "var(--font-mono)", fontStretch: "100%", fontWeight: 500, fontSize: "1.5rem" }}>{machine.hostname}</h1>
            <ProtectionPill status={machine.status} />
          </div>
          <p className="lede">
            {machine.osVersion} · {group?.name ?? t.noGroup} ·{" "}
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span className={`dot ${machine.online ? "on" : "off"}`} aria-hidden="true" />
              {machine.online ? t.agentOnline : t.seen(f.ago(machine.lastSeenAt))}
            </span>
          </p>
        </div>
        <div className="page-actions">
          {!revoked && (
            <button className="btn" type="button" onClick={backupNow} disabled={busy}>
              <Play aria-hidden="true" /> {t.backupNow}
            </button>
          )}
          {machine.status === "never" ? (
            <button className="btn btn-primary" type="button" disabled title={t.noSnapshotToRestore}>
              <RotateCcw aria-hidden="true" /> {t.restoreFiles}
            </button>
          ) : (
            <Link className="btn btn-primary" to={`/o/${orgId}/machines/${encodeURIComponent(machineId)}/restore`}>
              <RotateCcw aria-hidden="true" /> {t.restoreFiles}
            </Link>
          )}
        </div>
      </header>

      {machine.lastError && (
        <div className={`callout ${machine.status === "failed" ? "crit" : "warn"}`} role="status">
          <TriangleAlert aria-hidden="true" />
          <div>
            <strong>{machine.status === "failed" ? t.lastRunFailed : machine.status === "never" ? t.neverCompleted : t.backupOverdue}</strong>{" "}
            {machine.lastError}
          </div>
        </div>
      )}

      <Panel
        title={t.restorePoints}
        sub={policy ? t.last30WithRpo(policy.rpoHours) : t.last30}
        actions={<RestoreStripLegend />}
      >
        <RestoreStrip days={machine.restorePoints} size="big" />
      </Panel>

      <div className="grid-main-side">
        <section className="panel" style={{ minWidth: 0 }}>
          <Tabs.Root defaultValue="snapshots">
            <Tabs.List className="tabs-list" aria-label={t.detailsTabs}>
              <Tabs.Trigger value="snapshots">{t.tabSnapshots} {snaps.data && <span className="muted">({files.length})</span>}</Tabs.Trigger>
              {inventory.length > 0 && <Tabs.Trigger value="imagens">{t.tabImages} {snaps.data && <span className="muted">({images.length})</span>}</Tabs.Trigger>}
              <Tabs.Trigger value="bancos">{t.tabDatabases} {dbPoints.length > 0 && <span className="muted">({dbPoints.length})</span>}</Tabs.Trigger>
              <Tabs.Trigger value="compartilhamentos">{t.tabShares} {sharePoints.length > 0 && <span className="muted">({sharePoints.length})</span>}</Tabs.Trigger>
              <Tabs.Trigger value="atividade">{t.tabActivity}</Tabs.Trigger>
              <Tabs.Trigger value="detalhes">{t.tabAgent}</Tabs.Trigger>
            </Tabs.List>

            <Tabs.Content value="snapshots">
              {snaps.isPending ? <LoadingRows /> : snaps.isError ? <div className="panel-body"><ErrorState error={snaps.error} /></div> : files.length === 0 ? (
                <div className="empty"><History aria-hidden="true" />{t.noSnapshots}</div>
              ) : (
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>{t.colRestorePoint}</th>
                        <th className="right">{t.colTotal}</th>
                        <th className="right">{t.colNewData}</th>
                        <th>{t.colKeptBy}</th>
                        <th>{t.colImmutableUntil}</th>
                        <th><span className="sr-only">{t.colActions}</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {files.map((s) => (
                        <tr key={s.id}>
                          <td>
                            <div className="host">
                              <strong style={{ fontFamily: "var(--font-ui)", fontWeight: 600 }}>{t.snapshotWhen(f.weekdayDate(s.startedAt), f.time(s.startedAt))}</strong>
                              <span className="mono">{s.id.slice(0, 12)} · {s.trigger === "manual" ? t.triggerManual : t.triggerSchedule}{s.status === "partial" ? ` · ${t.partial}` : ""}</span>
                              {/* Ponto parcial: o relatório do que ficou de fora (ADR 0032). */}
                              {s.status === "partial" && <PartialReport orgId={orgId} snapshot={s} />}
                            </div>
                          </td>
                          <td className="right num">{f.bytes(s.totalBytes)}</td>
                          <td className="right num">{s.addedBytes > 0 ? f.bytes(s.addedBytes) : <span className="muted">—</span>}</td>
                          <td><div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{s.retention.map((r) => <span key={r} className="tag">{tc.retentionKind[r]}</span>)}</div></td>
                          <td className="num">
                            {s.immutableUntil ? <span className="tag"><Lock aria-hidden="true" />{f.fullDate(s.immutableUntil)}</span> : <span className="muted">—</span>}
                          </td>
                          <td className="right">
                            <Link className="btn btn-sm" to={`/o/${orgId}/machines/${encodeURIComponent(machineId)}/restore?snapshot=${s.id}`}>{t.restore}</Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Tabs.Content>

            {/* Imagem de disco (ADR 0022): cada volume é um arquivo bruto,
                então a ação é baixar, não navegar arquivos. */}
            <Tabs.Content value="imagens">
              {images.length === 0 ? (
                <div className="empty"><HardDrive aria-hidden="true" />{t.noImages}</div>
              ) : (
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>{t.colImage}</th>
                        <th>{t.colVolume}</th>
                        <th className="right">{t.colTotal}</th>
                        <th className="right">{t.colUsed}</th>
                        <th>{t.colConsistency}</th>
                        <th><span className="sr-only">{t.colActions}</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {images.flatMap((s) => (s.volumes.length ? s.volumes : [null]).map((v, i) => (
                        <tr key={`${s.id}-${v?.name ?? i}`}>
                          {i === 0 ? (
                            <td rowSpan={Math.max(s.volumes.length, 1)}>
                              <div className="host">
                                <strong style={{ fontFamily: "var(--font-ui)", fontWeight: 600 }}>{t.snapshotWhen(f.weekdayDate(s.startedAt), f.time(s.startedAt))}</strong>
                                <span className="mono">{s.sourcePath} · {s.trigger === "manual" ? t.triggerManual : t.triggerSchedule}</span>
                                <button className="btn btn-sm" type="button" style={{ marginTop: 6, justifySelf: "start" }}
                                  title={t.layoutHint} onClick={() => downloadVolume(s.id, "layout.json")}>
                                  <Download aria-hidden="true" /> {t.downloadLayout}
                                </button>
                              </div>
                            </td>
                          ) : null}
                          <td className="mono">{v ? `${v.name}${v.mount ? ` · ${v.mount}` : ""}${v.filesystem ? ` · ${v.filesystem}` : ""}` : "—"}</td>
                          <td className="right num">{v ? f.bytes(v.sizeBytes) : "—"}</td>
                          <td className="right num">{v?.usedBytes ? f.bytes(v.usedBytes) : <span className="muted">—</span>}</td>
                          <td>
                            {v?.consistency ? (
                              <span className="tag" title={v.consistency === "crash" ? t.consistencyCrashHint : undefined}>
                                {t.consistency[v.consistency]}
                              </span>
                            ) : <span className="muted">—</span>}
                          </td>
                          <td className="right">
                            {v && (
                              <button className="btn btn-sm" type="button" onClick={() => downloadVolume(s.id, v.name)}>
                                <Download aria-hidden="true" /> {t.downloadVolume}
                              </button>
                            )}
                          </td>
                        </tr>
                      )))}
                    </tbody>
                  </table>
                </div>
              )}
            </Tabs.Content>

            {/* Bancos de dados (ADR 0023): cada banco é um arquivo de dump. */}
            <Tabs.Content value="bancos">
              <MachineDatabases orgId={orgId} machineId={machineId} revoked={revoked} snapshots={dbPoints} jobs={jobs.data ?? []} />
            </Tabs.Content>

            {/* Compartilhamentos SMB (ADR 0030): esta máquina é a ponte para o NAS ou o servidor legado. */}
            <Tabs.Content value="compartilhamentos">
              <MachineShares orgId={orgId} machineId={machineId} revoked={revoked} snapshots={sharePoints} jobs={jobs.data ?? []} />
            </Tabs.Content>

            <Tabs.Content value="atividade">
              {jobs.isPending ? <LoadingRows /> : jobs.isError ? <div className="panel-body"><ErrorState error={jobs.error} /></div> : (
                <ul className="feed">
                  {jobs.data?.length === 0 && <li><span /><span className="muted">{t.noActivity}</span><span /></li>}
                  {jobs.data?.map((j) => (
                    <li key={j.id} style={{ gridTemplateColumns: "minmax(0,1fr) auto" }}>
                      <div style={{ minWidth: 0, display: "grid", gap: 4 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <strong>{tc.jobKind[j.kind]}</strong><JobPill status={j.status} />
                          {j.bytes > 0 && <span className="muted num">{f.bytes(j.bytes)}</span>}
                          {j.endedAt && <span className="muted num">{f.duration(Math.round((+new Date(j.endedAt) - +new Date(j.startedAt)) / 1000))}</span>}
                        </div>
                        <span className="ink-2" style={{ overflowWrap: "anywhere" }}>{j.message}</span>
                      </div>
                      <span className="when">{f.dateTime(j.startedAt)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Tabs.Content>

            <Tabs.Content value="detalhes">
              <div className="panel-body">
                <dl className="kv">
                  <dt>{t.system}</dt><dd>{osLabel[machine.os]} · {machine.osVersion} · {machine.arch}</dd>
                  <dt>{t.agent}</dt><dd className="mono">{machine.agentVersion}</dd>
                  <dt>{t.address}</dt><dd className="mono">{machine.ipAddress || "—"}</dd>
                  <dt>{t.enrolledAt}</dt><dd>{f.fullDate(machine.enrolledAt)}</dd>
                  <dt>{t.certValidUntil}</dt><dd>{f.fullDate(machine.certificateNotAfter)} <span className="muted">{t.autoRenewal}</span></dd>
                  <dt>{t.identifier}</dt><dd className="mono">{machine.id.split(":m-")[1]}</dd>
                  <dt>{t.sources}</dt><dd>{machine.sources.length ? machine.sources.map((s) => <div key={s} className="mono">{s}</div>) : <span className="muted">{t.sourcesFromPolicy}</span>}</dd>
                </dl>
              </div>
            </Tabs.Content>
          </Tabs.Root>
        </section>

        <div style={{ display: "grid", gap: 20, minWidth: 0 }}>
          <Panel title={t.volume}>
            <div className="stats">
              <div className="stat"><span className="v">{f.bytes(machine.protectedBytes)}</span><span className="k">{t.protected}</span></div>
              <div className="stat"><span className="v">{f.bytes(machine.storedBytes)}</span><span className="k">{t.stored}</span></div>
              <div className="stat"><span className="v">{machine.storedBytes ? f.ratio(machine.protectedBytes / machine.storedBytes) : "—"}</span><span className="k">{t.dedup}</span></div>
            </div>
          </Panel>

          {policy && (
            <Panel title={t.policy} actions={<Link className="btn btn-sm" to={`/o/${orgId}/policies/${encodeURIComponent(policy.id)}`}>{t.edit}</Link>}>
              <dl className="kv">
                <dt>{t.name}</dt>
                <dd>
                  {(policies.data?.length ?? 0) > 1 && !revoked ? (
                    <select className="select" aria-label={t.machinePolicy} id="maquina-politica" value={policy.id} disabled={busy} onChange={(e) => changePolicy(e.target.value)} style={{ height: 30 }}>
                      {policies.data?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  ) : policy.name}
                </dd>
                <dt>{t.retention}</dt>
                <dd>{retentionText(tc, policy.retention)}</dd>
                <dt>{t.totalSnapshots}</dt><dd className="num">{f.int(snaps.data?.length ?? 0)}</dd>
              </dl>
            </Panel>
          )}

          {!revoked && inventory.length > 0 && (
            <Panel title={t.disks} sub={disks.data?.updatedAt ? t.disksStale(f.ago(disks.data.updatedAt)) : undefined}>
              <div style={{ display: "grid", gap: 12 }}>
                <p className="ink-2" style={{ fontSize: "var(--fs-13)" }}>{t.disksHint}</p>
                {inventory.map((d) => (
                  <label className="check" key={d.id} htmlFor={`disco-${d.id}`}>
                    <input id={`disco-${d.id}`} type="checkbox" checked={imageDisks.includes(d.id)} disabled={savingDisks}
                      onChange={(e) => void saveDisks(e.target.checked ? [...imageDisks, d.id] : imageDisks.filter((x) => x !== d.id))} />
                    <span>
                      <strong className="mono">{d.id}</strong><br />
                      <span className="ink-2">{[d.model, t.diskSize(f.bytes(d.sizeBytes)), d.system ? t.diskSystem : ""].filter(Boolean).join(" · ")}</span>
                    </span>
                  </label>
                ))}
                <p className={imageDisks.length > 0 || policy?.image.enabled ? "ink-2" : "callout"} role="status" style={{ margin: 0, fontSize: "var(--fs-13)" }}>
                  {imageDisks.length > 0 ? t.disksScheduled(policy?.name ?? "", imageDisks.length)
                    : policy?.image.enabled ? t.disksPolicyDefault(policy.name) : t.disksNotScheduled}
                </p>
                <button className="btn" type="button" onClick={imageNow} disabled={busy}>
                  <HardDrive aria-hidden="true" /> {t.imageNow}
                </button>
              </div>
            </Panel>
          )}

          {!revoked && (
            <Panel title={t.dangerZone}>
              <div style={{ display: "grid", gap: 10 }}>
                <p className="ink-2" style={{ fontSize: "var(--fs-13)" }}>
                  {t.revokeExplain}
                </p>
                <button className="btn btn-danger" type="button" onClick={() => setConfirmRevoke(true)}>
                  <Ban aria-hidden="true" /> {t.revokeMachine}
                </button>
              </div>
            </Panel>
          )}
        </div>
      </div>

      <Dialog
        open={confirmRevoke}
        onOpenChange={setConfirmRevoke}
        title={t.revokeTitle(machine.hostname)}
        description={t.revokeDescription}
        footer={
          <>
            <button className="btn" type="button" onClick={() => setConfirmRevoke(false)}>{tc.cancel}</button>
            <button className="btn btn-primary" style={{ background: "var(--crit-mark)", borderColor: "var(--crit-mark)" }} type="button" onClick={revoke} disabled={busy}>
              {t.revokeMachine}
            </button>
          </>
        }
      />
    </>
  );
}

// PartialReport é o relatório de um ponto parcial: quantos itens ficaram de
// fora e quantos arquivos entraram, os grupos por pasta e erro (o que a
// pessoa precisa para agir) e, sob demanda, a lista guardada e o CSV.
function PartialReport({ orgId, snapshot }: { orgId: string; snapshot: Snapshot }) {
  const t = useT(machineDetailMessages);
  const f = useFormat();
  const api = useApi();
  const [list, setList] = useState<{ status: "idle" | "loading" | "ok" | "error"; data?: SnapshotErrors }>({ status: "idle" });
  const csv = `/api/v1/orgs/${encodeURIComponent(orgId)}/snapshots/${encodeURIComponent(snapshot.id)}/errors?format=csv`;

  async function load() {
    if (list.status !== "idle") return;
    setList({ status: "loading" });
    try {
      setList({ status: "ok", data: await api.snapshotErrors(orgId, snapshot.id) });
    } catch {
      setList({ status: "error" });
    }
  }
  const entries = list.data?.entries ?? snapshot.errors;
  const listed = list.data?.listed ?? snapshot.errors.length;

  return (
    <div className="partial-report" style={{ display: "grid", gap: 8, marginTop: 6, fontSize: "var(--fs-12)" }}>
      <span className="ink-2">{t.partialReport(snapshot.errorTotal, snapshot.fileCount)}</span>
      {snapshot.errorGroups.length > 0 && (
        <table className="data compact" aria-label={t.partialGroupsTitle}>
          <thead>
            <tr><th>{t.partialColDir}</th><th>{t.partialColError}</th><th className="right">{t.partialColCount}</th></tr>
          </thead>
          <tbody>
            {snapshot.errorGroups.map((g) => (
              <tr key={`${g.dir}|${g.error}`}>
                <td className="mono">{g.dir === "*" ? t.partialOtherDirs : g.dir === "." ? t.partialRoot : g.dir}</td>
                <td>{g.error}</td>
                <td className="right num">{f.int(g.count)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <details className="grants" onToggle={(e) => { if (e.currentTarget.open) void load(); }}>
        <summary>{t.partialListTitle(listed, snapshot.errorTotal)}</summary>
        {list.status === "loading" && <span className="ink-2">{t.partialListLoading}</span>}
        {list.status === "error" && <span className="error">{t.partialListFailed}</span>}
        {list.status !== "loading" && entries.length === 0 && <span className="ink-2">{t.partialListEmpty}</span>}
        {entries.length > 0 && (
          <ul className="plain-list" style={{ maxHeight: 320, overflow: "auto" }}>
            {entries.map((e) => <li key={e.path} style={{ padding: "4px 0" }}><span className="mono">{e.path}</span> <span className="ink-2">— {e.error}</span></li>)}
          </ul>
        )}
        {snapshot.errorTotal > 0 && <a className="btn btn-sm" href={csv} download><Download aria-hidden="true" />{t.partialCsv}</a>}
      </details>
    </div>
  );
}
